import { ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import { cleanEvt, prevent } from '../utils/event.js'

// Static props for useAnchor
export const useAnchorStaticProps = {
  target: {
    type: [Boolean, String],
    default: true
  },
  noParentEvent: Boolean
}

export default function ({ avoidEmit, configureAnchorEl }) {
  // Get the current instance of the component
  const { props, proxy, emit } = getCurrentInstance()

  // Reference to the anchor element
  const anchorEl = ref(null)
  let touchTimer = null

  // Function to check if the element can be shown
  function canShow(evt) {
    return anchorEl.value === null
      ? false
      : evt === undefined ||
          evt.touches === undefined ||
          evt.touches.length <= 1
  }

  const anchorEvents = {}

  // If configureAnchorEl is not provided, set default event handlers
  if (configureAnchorEl === undefined) {
    Object.assign(anchorEvents, {
      hide(evt) {
        proxy.hide(evt)
      },
      toggle(evt) {
        proxy.toggle(evt)
        evt.qAnchorHandled = true
      },
      prevent
    })
  }

  // Function to remove event listeners from the anchor element
  function unconfigureAnchorEl() {
    cleanEvt(anchorEvents, 'anchor')
  }

  // Function to set and configure the anchor element
  function setAnchorEl(el) {
    anchorEl.value = el
    while (anchorEl.value.classList.contains('q-anchor--skip')) {
      anchorEl.value = anchorEl.value.parentNode
    }
    configureAnchorEl()
  }

  // Function to pick the anchor element based on props.target
  function pickAnchorEl() {
    if (
      props.target === false ||
      props.target === '' ||
      proxy.$el.parentNode === null
    ) {
      anchorEl.value = null
    } else if (props.target === true) {
      setAnchorEl(proxy.$el.parentNode)
    } else {
      let el = props.target
      if (typeof props.target === 'string') {
        try {
          el = document.querySelector(props.target)
        } catch (err) {
          el = undefined
        }
      }
      if (el !== undefined && el !== null) {
        anchorEl.value = el.$el || el
        configureAnchorEl()
      } else {
        anchorEl.value = null
        console.error(`Anchor: target "${props.target}" not found`)
      }
    }
  }

  // Watchers to react to changes in props and reconfigure the anchor element
  watch(
    () => props.contextMenu,
    (val) => {
      if (anchorEl.value !== null) {
        unconfigureAnchorEl()
        configureAnchorEl(val)
      }
    }
  )

  watch(
    () => props.target,
    () => {
      if (anchorEl.value !== null) {
        unconfigureAnchorEl()
      }
      pickAnchorEl()
    }
  )

  watch(
    () => props.noParentEvent,
    (val) => {
      if (anchorEl.value !== null) {
        if (val === true) {
          unconfigureAnchorEl()
        } else {
          configureAnchorEl()
        }
      }
    }
  )

  // onMounted hook to initialize the anchor element when the component is mounted
  onMounted(() => {
    pickAnchorEl()
    if (
      avoidEmit !== true &&
      props.modelValue === true &&
      anchorEl.value === null
    ) {
      emit('update:modelValue', false)
    }
  })

  // onBeforeUnmount hook to clean up before the component is destroyed
  onBeforeUnmount(() => {
    touchTimer !== null && clearTimeout(touchTimer)
    unconfigureAnchorEl()
  })

  return {
    anchorEl,
    canShow,
    anchorEvents
  }
}
